import React, { useEffect, useState, useCallback } from "react";
import { fetchActivePlans } from "../../services/planService";
import { fetchActiveSpeakers } from "../../services/adminSpeakerServices";
import { createDayWithSessions } from "../../services/adminTimeTableServices";
import { fetchTemplates } from "../../services/adminTemplateService";
import ContentPicker from "./ContentPicker";
import DurationTimePicker, { computeEndTime } from "./DurationTimePicker";

const adminTZ  = Intl.DateTimeFormat().resolvedOptions().timeZone;
const LEVELS   = ["BEGINNER", "INTERMEDIATE", "ADVANCED"];
const STATUSES = ["DRAFT", "ACTIVE", "INACTIVE"];

const blankRow = (prev = null) => ({
  topic:        "",
  status:       "DRAFT",
  level:        prev?.level       || "",
  startTime:    prev ? prev.endTime : "",
  durationHrs:  prev?.durationHrs  ?? 0,
  durationMins: prev?.durationMins ?? 30,
  planId:       prev?.planId       || "",
  totalSlots:   prev?.totalSlots   || 1,
  speakerId:    prev?.speakerId    || "",
  contentType:  "",
  contentId:    null,
  contentLabel: null,
});

const buildISO = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const hhmm = timeStr.slice(0, 5); // ensure HH:mm only, strip any seconds
  const off  = -new Date().getTimezoneOffset();
  const sign = off >= 0 ? "+" : "-";
  const abs  = Math.abs(off);
  const oh   = String(Math.floor(abs / 60)).padStart(2, "0");
  const om   = String(abs % 60).padStart(2, "0");
  return `${dateStr}T${hhmm}:00${sign}${oh}:${om}`;
};

// ── Shared field styles ───────────────────────────────────────────────────────
const lbl = {
  display: "block", fontSize: "0.72rem", fontWeight: 700, color: "#475569",
  marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.03em",
};
const inp = {
  width: "100%", padding: "7px 10px", borderRadius: 6, border: "1px solid #e2e8f0",
  fontSize: "0.85rem", color: "#0f172a", background: "#fff", boxSizing: "border-box",
};

// ── Single session card ───────────────────────────────────────────────────────
const SessionCard = ({ row, idx, total, plans, speakers, onChange, onRemove }) => {
  const set = (field, value) => onChange(idx, field, value);

  return (
    <div style={{
      background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
      padding: "16px 18px", marginBottom: 12,
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      {/* Card header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontSize: "0.8rem", fontWeight: 700, color: "#64748b" }}>
          Session {idx + 1}
        </span>
        <button className="tt-icon-btn danger" onClick={() => onRemove(idx)}
          disabled={total === 1} title="Remove session"
          style={{ width: 26, height: 26, fontSize: 12 }}>✕</button>
      </div>

      {/* Row 1: Status + Topic */}
      <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Status</label>
          <select style={inp} value={row.status} onChange={(e) => set("status", e.target.value)}>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Topic <span style={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
          <input style={inp} placeholder="e.g. Fluency Practice" value={row.topic}
            onChange={(e) => set("topic", e.target.value)} />
        </div>
      </div>

        {/* Row 2: Level + DurationTimePicker */}
        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Level</label>
          <select style={{ ...inp, marginBottom: 12 }} value={row.level} onChange={(e) => set("level", e.target.value)}>
            <option value="">— Select Level —</option>
            {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <DurationTimePicker
            startTime={row.startTime}
            durationHrs={row.durationHrs}
            durationMins={row.durationMins}
            endTime={computeEndTime(row.startTime, row.durationHrs, row.durationMins)}
            onStartChange={(v) => set("startTime", v)}
            onDurationChange={(h, m) => { set("durationHrs", h); set("durationMins", m); }}
          />
        </div>

      {/* Row 3: Plan + Slots + Speaker */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={lbl}>Plan</label>
          <select style={inp} value={row.planId} onChange={(e) => set("planId", e.target.value)}>
            <option value="">— Select Plan —</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.planName} ({p.planType})</option>)}
          </select>
        </div>
        <div>
          <label style={lbl}>Slots</label>
          <input type="number" min={1} style={inp} value={row.totalSlots}
            onChange={(e) => set("totalSlots", e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Speaker <span style={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
          <select style={inp} value={row.speakerId} onChange={(e) => set("speakerId", e.target.value)}>
            <option value="">— Select Speaker —</option>
            {speakers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      {/* Row 4: Content Type */}
      <ContentPicker
        contentType={row.contentType}
        contentId={row.contentId}
        contentLabel={row.contentLabel}
        onTypeChange={(type) => {
          set("contentType", type);
          set("contentId", null);
          set("contentLabel", null);
        }}
        onContentChange={(val) => {
          set("contentId",    val?.id    || null);
          set("contentLabel", val?.label || null);
        }}
      />
    </div>
  );
};

// ── Main Drawer ───────────────────────────────────────────────────────────────
const ScheduleDayDrawer = ({ onClose, reload }) => {
  const [date, setDate]           = useState("");
  const [plans, setPlans]         = useState([]);
  const [speakers, setSpeakers]   = useState([]);
  const [templates, setTemplates] = useState([]);
  const [rows, setRows]           = useState([blankRow()]);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    fetchActivePlans().then((r)    => setPlans(r.data || []));
    fetchActiveSpeakers().then((r) => setSpeakers(r.data || []));
    fetchTemplates().then((r)      => { if (r.status === 200) setTemplates(r.data || []); });
  }, []);

  const applyTemplate = useCallback((templateId) => {
    const tpl = templates.find((t) => String(t.id) === String(templateId));
    if (!tpl) return;
    const sorted = [...(tpl.sessions || [])].sort((a, b) => a.sortOrder - b.sortOrder);
    setRows(sorted.map((s) => {
      const start = s.startTime ? s.startTime.slice(0, 5) : "";
      const end   = s.endTime   ? s.endTime.slice(0, 5)   : "";
      let durationHrs = 0, durationMins = 30;
      if (start && end) {
        const [sh, sm] = start.split(":").map(Number);
        const [eh, em] = end.split(":").map(Number);
        const diff = (eh * 60 + em) - (sh * 60 + sm);
        durationHrs  = Math.max(0, Math.floor(diff / 60));
        durationMins = Math.max(0, diff % 60);
      }
      return {
        topic:        "",
        status:       "DRAFT",
        level:        s.level      || "",
        startTime:    start,
        durationHrs,
        durationMins,
        planId:       s.planId     || "",
        totalSlots:   s.totalSlots || 1,
        speakerId:    s.speakerId  || "",
        contentType:  "",
        contentId:    null,
        contentLabel: null,
      };
    }));
  }, [templates]);

  const updateRow = (idx, field, value) =>
    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

  const addRow    = () => setRows((prev) => [...prev, blankRow(prev[prev.length - 1])]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!date) return alert("Please select a date.");
    if (rows.length === 0) return alert("Add at least one session.");

    const sessions = rows.map((r) => ({
      topic:       r.topic       || null,
      level:       r.level       || null,
      planId:      r.planId      ? Number(r.planId)    : null,
      speakerId:   r.speakerId   ? Number(r.speakerId) : null,
      totalSlots:  Number(r.totalSlots) || 1,
      status:      r.status      || "DRAFT",
      contentType: r.contentType || null,
      contentId:   r.contentId   || null,
      startTime:   buildISO(date, r.startTime),
      endTime:     buildISO(date, computeEndTime(r.startTime, r.durationHrs, r.durationMins)),
    }));

    setSaving(true);
    try {
      await createDayWithSessions({ date, timeZone: adminTZ, sessions });
      reload();
      onClose();
    } catch (err) {
      const msg = err.response?.data?.message || "";
      if (err.response?.status === 409 || msg.toLowerCase().includes("already")) {
        alert(`A timetable day already exists for ${date}. Please choose a different date.`);
      } else {
        alert(msg || "Failed to create day.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tt-drawer-overlay" onClick={onClose}>
      <div className="tt-drawer" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="tt-drawer-header">
          <h2>📅 Schedule New Day</h2>
          <button className="tt-drawer-close" onClick={onClose}>✕</button>
        </div>

        {/* Body */}
        <div className="tt-drawer-body">

          {/* Date + Template */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 24 }}>
            <div className="tt-field">
              <label>Date *</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="tt-field">
              <label>
                Start from Template{" "}
                <span style={{ color: "#94a3b8", fontWeight: 400, textTransform: "none" }}>(optional)</span>
              </label>
              <select defaultValue="" onChange={(e) => applyTemplate(e.target.value)}>
                <option value="" disabled>— Select a template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name} ({t.sessions?.length || 0} sessions)</option>
                ))}
              </select>
            </div>
          </div>

          {/* Session cards */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <p className="tt-section-label" style={{ margin: 0 }}>
              Sessions ({rows.length})
            </p>
          </div>

          {rows.map((row, idx) => (
            <SessionCard
              key={idx}
              row={row}
              idx={idx}
              total={rows.length}
              plans={plans}
              speakers={speakers}
              onChange={updateRow}
              onRemove={removeRow}
            />
          ))}

          <button className="tt-add-row-btn" onClick={addRow}>+ Add Session</button>
        </div>

        {/* Footer */}
        <div className="tt-drawer-footer">
          <button className="adm-btn adm-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="adm-btn adm-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Create Day & Sessions"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleDayDrawer;
